import React from 'react';
import { motion } from 'framer-motion';
import quickbotImg from '../assets/quickbot/quickbot_2.png';
import leadImg from '../assets/quickbot/lead_capture.png';
import bookingImg from '../assets/quickbot/booking_faq.png';
import supportImg from '../assets/quickbot/24x7_support.png';

const QuickBot = () => {
  return (
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="max-w-6xl mx-auto px-6 py-16"
    >   
    <img src={quickbotImg} alt="QuickBot Logo" className='w-full h-20 object-contain mb-4'></img>
      <h1 className="text-4xl font-bold text-center text-blue-700 mb-10">
        Your Smart Chatbot for WhatsApp
      </h1>

      <p className="text-center text-lg text-gray-600 max-w-3xl mx-auto mb-14">
        QuickBot automates customer conversations — from capturing leads to booking appointments, resolving FAQs, and giving 24×7 support. No coding needed.
      </p>

      {/* Visual Use Cases */}
      <div className="grid md:grid-cols-3 gap-8 mb-20">
        <div className="bg-white rounded-xl shadow-xl p-6 text-center">
          <img src={leadImg} alt="Lead Capture" className="w-full h-40 object-contain mb-4" />
          <h3 className="text-xl font-bold text-blue-700 mb-2">Capture Leads Instantly</h3>
          <p className="text-sm text-gray-600">
            Ask for name, number, email & sync directly to your CRM. Run forms right inside WhatsApp.
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-xl p-6 text-center">
          <img src={bookingImg} alt="Booking Flow" className="w-full h-40 object-contain mb-4" />
          <h3 className="text-xl font-bold text-blue-700 mb-2">Automate Bookings & FAQs</h3>
          <p className="text-sm text-gray-600">
            Let users book appointments or get quick answers from menu-driven flows. No human effort needed.
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-xl p-6 text-center">
          <img src={supportImg} alt="24x7 Support" className="w-full h-40 object-contain mb-4" />
          <h3 className="text-xl font-bold text-blue-700 mb-2">24×7 Smart Support</h3>
          <p className="text-sm text-gray-600">
            Answer queries even when your business is closed. QuickBot handles chats around the clock.
          </p>
        </div>
      </div>

      {/* Why Use QuickBot – Zigzag Style */}
        <div className="mb-20">
        <h2 className="text-3xl font-bold text-blue-700 text-center mb-12">Why Use QuickBot?</h2>

        <div className="space-y-14">
            {/* Feature 1 */}
            <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
            <div className="md:w-1/2 flex items-start space-x-4">
                <div className="text-4xl text-blue-600">⚙️</div>
                <div>
                <h3 className="text-xl font-semibold text-blue-700 mb-1">Build Chatbots Without Code</h3>
                <p className="text-gray-600 text-base">
                    Create chat flows in minutes with our no-code builder. No tech skills required.
                </p>
                </div>
            </div>
            <div className="md:w-1/2 flex items-start space-x-4">
                <div className="text-4xl text-green-600">📱</div>
                <div>
                <h3 className="text-xl font-semibold text-blue-700 mb-1">Works Seamlessly on WhatsApp</h3>
                <p className="text-gray-600 text-base">
                    No need for external apps — your bot lives directly on your WhatsApp number.
                </p>
                </div>
            </div>
            </div>

            {/* Feature 2 */}
            <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
            <div className="md:w-1/2 flex items-start space-x-4">
                <div className="text-4xl text-purple-600">🔁</div>
                <div>
                <h3 className="text-xl font-semibold text-blue-700 mb-1">Auto Sync with Your CRM</h3>
                <p className="text-gray-600 text-base">
                    Capture and forward leads automatically to Google Sheets, Zoho, or HubSpot.
                </p>
                </div>
            </div>
            <div className="md:w-1/2 flex items-start space-x-4">
                <div className="text-4xl text-orange-500">❓</div>
                <div>
                <h3 className="text-xl font-semibold text-blue-700 mb-1">Instant FAQ Responses</h3>
                <p className="text-gray-600 text-base">
                    Let your chatbot handle repetitive queries automatically, 24×7.
                </p>
                </div>
            </div>
            </div>

            {/* Feature 3 */}
            <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
            <div className="md:w-1/2 flex items-start space-x-4">
                <div className="text-4xl text-red-500">⚡</div>
                <div>
                <h3 className="text-xl font-semibold text-blue-700 mb-1">Fast Setup</h3>
                <p className="text-gray-600 text-base">
                    Set up a working chatbot in under 10 minutes, with full onboarding support.
                </p>
                </div>
            </div>
            <div className="md:w-1/2 flex items-start space-x-4">
                <div className="text-4xl text-yellow-500">💸</div>
                <div>
                <h3 className="text-xl font-semibold text-blue-700 mb-1">Affordable for All</h3>
                <p className="text-gray-600 text-base">
                    Get powerful automation without breaking the bank — perfect for growing teams.
                </p>
                </div>
            </div>
            </div>
        </div>
        </div>


      {/* CTA */}
      <div className="bg-blue-50 rounded-2xl p-10 text-center shadow-xl">
        <h3 className="text-2xl font-bold text-blue-700 mb-3">
          Let QuickBot handle your chats while you focus on growth
        </h3>
        <p className="text-gray-600 mb-6">
          Start for free or explore our pricing plans tailored for small businesses.
        </p>
        <a
          href="/pricing"
          className="inline-block bg-blue-700 text-white px-6 py-3 rounded-full font-semibold text-sm hover:bg-blue-800"
        >
          View Pricing
        </a>
      </div>
    </motion.section>
  );
};

export default QuickBot;
